// Generic Data-Master CRUD tab factory for simple entities.
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ADMIN } from '@/constants/testIds';

export function GenericMasterTab({
  title, description, endpoint, fields, columns, createTestIdName,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const emptyForm = () =>
    Object.fromEntries(fields.map(f => [f.key, f.default ?? '']));

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get(endpoint); setRows(data); }
    catch { toast.error(`Failed to load ${title.toLowerCase()}`); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [endpoint]);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm(), ...r }); setOpen(true); };
  const save = async () => {
    try {
      const payload = { ...form };
      if (editing) await api.patch(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success('Saved'); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.name || r.id}?`)) return;
    try { await api.delete(`${endpoint}/${r.id}`); load(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <AdminHeader
        title={title}
        description={description}
        onCreate={openCreate}
        createLabel={`New ${title.replace(/s$/, '')}`}
        createTestId={ADMIN.createButton(createTestIdName)}
      />
      <AdminTable
        loading={loading} rows={rows}
        columns={columns}
        rowTestId={ADMIN.row}
        onEdit={openEdit} onDelete={remove}
      />
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${title.replace(/s$/, '')}` : `New ${title.replace(/s$/, '')}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="text-xs uppercase tracking-wider text-stone-600">{f.label}</Label>
                <Input
                  type={f.type || 'text'}
                  value={form[f.key] ?? ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  className="h-9"
                  data-testid={`${createTestIdName}-field-${f.key}`}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid={ADMIN.saveButton}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function BrandsTab() {
  return (
    <GenericMasterTab
      title="Brands"
      description="Hearing aid manufacturer brands (Signia, Phonak, Bernafon, etc.)"
      endpoint="/brands"
      createTestIdName="brand"
      fields={[{ key: 'name', label: 'Name' }, { key: 'code', label: 'Code' }]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'code', label: 'Code' },
        { key: 'fundle_id', label: 'Fundle ID', render: (r) => r.fundle_id ? <span className="font-mono text-xs">{r.fundle_id}</span> : '—' },
      ]}
    />
  );
}

export function SourcesTab() {
  return (
    <GenericMasterTab
      title="Sources"
      description="Lead sources (Doctors, Hear.com, HearClear, Hospital, Partners)."
      endpoint="/sources"
      createTestIdName="source"
      fields={[{ key: 'name', label: 'Name' }]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'fundle_id', label: 'Fundle ID', render: (r) => r.fundle_id ? <span className="font-mono text-xs">{r.fundle_id}</span> : '—' },
      ]}
    />
  );
}

export function SubsourcesTab() {
  return (
    <GenericMasterTab
      title="Sub-sources"
      description="Specific lead sub-sources (Camps, Website, Patient Referral, Hear.com, etc.)"
      endpoint="/subsources"
      createTestIdName="subsource"
      fields={[
        { key: 'name', label: 'Name' },
        { key: 'source_name', label: 'Parent source' },
      ]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'source_name', label: 'Source' },
        { key: 'fundle_id', label: 'Fundle ID', render: (r) => r.fundle_id ? <span className="font-mono text-xs">{r.fundle_id}</span> : '—' },
      ]}
    />
  );
}

export function ConsultantsTab() {
  return (
    <GenericMasterTab
      title="Consultants"
      description="Doctors and consultants (ENT, referrers) synced from Fundle."
      endpoint="/consultants"
      createTestIdName="consultant"
      fields={[
        { key: 'name', label: 'Name' },
        { key: 'speciality', label: 'Speciality' },
        { key: 'phone', label: 'Phone' },
        { key: 'email', label: 'Email' },
      ]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'speciality', label: 'Speciality' },
        { key: 'phone', label: 'Phone' },
      ]}
    />
  );
}

export function ItemsTab() {
  return (
    <GenericMasterTab
      title="Items (Inventory)"
      description="Hearing aid SKUs, accessories, chargers, and trial devices."
      endpoint="/items"
      createTestIdName="item"
      fields={[
        { key: 'name', label: 'Name' },
        { key: 'code', label: 'Code' },
        { key: 'category', label: 'Category (HA / ACC / CIC / BTE)' },
      ]}
      columns={[
        { key: 'name', label: 'Name' },
        { key: 'code', label: 'Code' },
        { key: 'category', label: 'Category' },
      ]}
    />
  );
}
